import { PaymentBatchReviewState } from "../utils";
import { ApprovalsBannerConfig } from "./types";

/**
 * Maps the review state to banner configuration.
 * Pure mapping - no additional logic derivation.
 */
export function getApprovalsBannerConfig(
  reviewState: PaymentBatchReviewState,
  isGPX: boolean,
): ApprovalsBannerConfig {
  const {
    hasUserAlreadyApproved,
    isFullyReviewed,
    approvalsRemaining,
    numApprovalsRequired,
    approvalsReceived,
    hasApprovePaymentPermission,
    isCreatorAndCannotReviewOwn,
  } = reviewState;

  const title = `${approvalsReceived} of ${numApprovalsRequired} approvals received`;

  // Fully approved
  if (isFullyReviewed) {
    return {
      shouldShow: false,
    };
  }

  // User has already approved
  if (hasUserAlreadyApproved) {
    return {
      shouldShow: true,
      variant: "primary",
      title,
      description: `You have already approved this payment batch. ${approvalsRemaining} more needed.`,
    };
  }

  // Check permission BEFORE checking creator status
  // This ensures users without approval permissions see the correct message
  if (!hasApprovePaymentPermission) {
    return {
      shouldShow: true,
      variant: "warning",
      title,
      description:
        "This payment requires approval from a payment manager or payment administrator.",
    };
  }

  // User is the creator and cannot review their own batch
  if (isCreatorAndCannotReviewOwn) {
    const title = "You cannot approve this payment batch because you created it";
    const description = isGPX
      ? "This payment requires approval from a payment manager or payment administrator."
      : "Another user must provide approval.";

    return {
      shouldShow: true,
      variant: "warning",
      title,
      description,
    };
  }

  // Don't show banner if only 1 or fewer approvals required
  if (numApprovalsRequired <= 1) {
    return {
      shouldShow: false,
    };
  }

  // Default: awaiting review, user can approve
  return {
    shouldShow: true,
    variant: "primary",
    title,
    description: `${approvalsRemaining} more needed before this payment batch can be processed.`,
  };
}
